import { generatePath } from 'react-router-dom';

import { ActionText } from '@components/action-text';
import { Alert } from '@components/alert';
import { HideContainer } from '@components/hide-container';
import { IconAttributeJourney } from '@components/icons/attribute-journey';
import { IconSend } from '@components/icons/send';
import { LabelTextGroup } from '@components/label-text-group';
import { RelativeSpinner, Spinner } from '@components/loading-spinner';
import { NoResultCard } from '@components/no-result-card';
import { ProgressBar } from '@components/progress-bar';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { FieldType } from '@components/text-editors/addons/field-mapping';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';
import { formatNumber } from '@utils/numbers';

import { AttributeFieldType } from 'api/attributes/requests';
import { createTravellerJourneyActionFieldPath } from 'api/documents/requests';
import { useJourney, useJourneyAttributes } from 'api/journeys/hooks';
import {
  useActivityLogs,
  useJourneyTravellerAction,
  useUpdateJourneyTravellerAction,
} from 'api/traveller-actions/hooks';
import { useJourneyTraveller } from 'api/traveller-journeys/hooks';
import { JourneyField, TravellerJourneyActionDetailed } from 'api/types';
import { useUsers } from 'api/users/hooks';
import { ACTION_TYPES } from 'ui/actions/action-config';
import { routePaths } from 'ui/authenticated/navigation';

import { AddToJourneyFormContent, AddToJourneyInfoBox } from './helpers';

import {
  ActionProgressBar,
  RenderAttributeFieldLabelGroup,
  SidebarProps,
  TravellerActionActions,
  TravellerActionFooter,
  TravellerActionHeader,
  formatDecimal,
  isActionError,
  useActionTabs,
  useEditingContext,
} from '../common';

const ACTION_TYPE = ACTION_TYPES.add_to_journey;

const formatOutputFieldValue = (value: unknown, type?: FieldType): string => {
  if (value == null || value === '') return '';
  switch (type) {
    case 'date':
      return abbreviateDate(value as string, 'en');
    case 'number':
      return formatNumber(value as number);
    case 'decimal':
      return formatDecimal(value);
    case 'file':
      if (typeof value === 'object' && !Array.isArray(value)) {
        return (value as { name?: string }).name ?? '';
      }
      return String(value);
    case 'file_multi':
      if (Array.isArray(value)) {
        return value.map((item) => (item as { name?: string }).name ?? String(item)).join('\n');
      }
      return String(value);
    default:
      return String(value);
  }
};

export const SidebarContent = ({ shortId }: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);

  const travellerActionQuery = useJourneyTravellerAction<typeof ACTION_TYPE>(shortId);
  const { data: activityLogs } = useActivityLogs(shortId);
  const activityCount = activityLogs?.length ?? 0;
  const action = travellerActionQuery.data;

  const { tabs } = useActionTabs({
    actionId: shortId ?? '',
    activityCount,
    display: action ? <DetailsContent action={action} /> : <></>,
    edit: action ? <FormContent action={action} /> : <></>,
  });

  if (travellerActionQuery.isLoading) {
    return <Spinner />;
  }
  if (!shortId || !action) {
    return <div>{t('dataNotFound')}</div>;
  }

  return (
    <Sidebar>
      <TravellerActionHeader actionType={ACTION_TYPE} tabs={tabs}>
        <TravellerActionActions
          action={action}
          ratioOverride={action.status === 'completed' ? 1 : 0}
          statusOverride={action.status}
        />
      </TravellerActionHeader>
    </Sidebar>
  );
};

type ContentProps = {
  action: TravellerJourneyActionDetailed<typeof ACTION_TYPE>;
};

const DetailsContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const status = action.status;
  const isError = isActionError(action);
  const selectedJourney = action.values?.journeyId ?? '';

  const createdData = (action.data.createdData ?? {}) as {
    journeyId: string;
    journeyName: string;
    startJourney: boolean;
    fields: {
      field: JourneyField;
      field_title: string;
      field_type: AttributeFieldType;
      option_value: string;
    }[];
  };

  const { data: journey, isError: isJourneyError, isLoading } = useJourney(selectedJourney);
  const fields = (action.data?.fields ?? []) as JourneyField[];

  const attributeTemplatesQuery = useJourneyAttributes(selectedJourney);
  const attributes = attributeTemplatesQuery.data ?? [];

  const userQuery = useUsers();
  const usersList = userQuery.data;

  const createdTravellerJourneyId =
    action.data?.createdTravellerJourneyId ?? action.data?.waitForTravellerJourneyId;
  const { data: journeyTraveller, isError: isJourneyTravellerError } =
    useJourneyTraveller(createdTravellerJourneyId);

  if (isLoading || attributeTemplatesQuery.isLoading) {
    return <RelativeSpinner />;
  }

  const fieldIds = fields.map((field) => field.field);
  const attributesMissing = attributes.filter(
    (attribute) => attribute.isActive && !fieldIds.includes(attribute.shortId)
  );

  const hasAttribute = (field: JourneyField) => {
    return !!attributes.find((attribute) => attribute.shortId === field.field);
  };

  const noValidAttributes =
    status !== 'completed' &&
    fields.length > 0 &&
    fields.filter((field) => hasAttribute(field)).length === 0;

  const hasSubJourneyVariables =
    status === 'completed' && action.variables && Object.keys(action.variables).length > 0;

  return (
    <Stack space={6}>
      <ActionProgressBar action={action} isError={isError} isCard={false} />
      <Sidebar.Separator />
      <HideContainer title={t('actionDetails')}>
        <LabelTextGroup label={t('description')} text={action.description} />
      </HideContainer>
      <Sidebar.Separator />
      <HideContainer title={t('travellerDetails')}>
        <Stack space={2}>
          <LabelTextGroup
            label={t('travellerName')}
            text={action.data?.travellerName ?? action.travellerJourney?.name}
          />
          <LabelTextGroup
            label={t('travellerEmail')}
            text={action.data?.travellerEmail ?? action.travellerJourney?.email}
          />
        </Stack>
      </HideContainer>
      <Sidebar.Separator />
      <HideContainer title={t('journeyDetails')}>
        <Stack space={2}>
          {isJourneyError && <Alert variant="info">{t('journeyNotFoundInfo')}</Alert>}
          {journey && createdTravellerJourneyId ? (
            <Stack space={1}>
              <Text font="semibold14" text={t('journey')} />
              <ActionText.Link
                font="regular14"
                underline
                to={generatePath(routePaths.traveller, {
                  workspaceId: journey.workspace,
                  journeyId: selectedJourney,
                  travellerId: createdTravellerJourneyId,
                  '*': null,
                })}
              >
                {journey.title}
              </ActionText.Link>
            </Stack>
          ) : (
            <LabelTextGroup
              label={t('journey')}
              text={
                journey?.title ??
                createdData.journeyName ??
                action.values?.lastSavedTitle ??
                t('journeyNotFound')
              }
            />
          )}
          {createdData.journeyId && (
            <>
              {isJourneyTravellerError ? (
                <Alert variant="info">{t('travellerNotInJourney')}</Alert>
              ) : journeyTraveller ? (
                <Stack space={2}>
                  <Text font="semibold14" text={t('progress')} />
                  <ProgressBar
                    ratio={journeyTraveller.progress}
                    variant={
                      journeyTraveller.status === 'completed'
                        ? 'default'
                        : journeyTraveller.status === 'paused'
                        ? 'paused'
                        : 'info'
                    }
                  />
                  <Text
                    font="regular12"
                    color="textSecondary"
                    text={`${Math.round(journeyTraveller.progress * 100)}% • ${t(
                      journeyTraveller.status
                    )}`}
                  />
                </Stack>
              ) : null}
            </>
          )}
        </Stack>
      </HideContainer>
      {fields.length > 0 && (
        <>
          <Sidebar.Separator />
          <HideContainer title={t('fields')}>
            <Stack space={4}>
              {noValidAttributes && attributesMissing.length > 0 && (
                <NoResultCard icon={IconAttributeJourney} text={t('noValidJourneyAttributes')} />
              )}
              {!noValidAttributes &&
                fields.map((field, idx) => (
                  <RenderAttributeFieldLabelGroup
                    key={idx}
                    attribute={attributes.find((attribute) => attribute.shortId === field.field)}
                    field={field}
                    usersList={usersList}
                    savedFieldName={
                      (createdData.fields ?? []).find(
                        (ufield) => ufield.field.field === field.field
                      )?.field_title
                    }
                    savedFieldType={
                      (createdData.fields ?? []).find(
                        (ufield) => ufield.field.field === field.field
                      )?.field_type ?? field.last_saved_type
                    }
                    savedOptionValue={
                      (createdData.fields ?? []).find(
                        (ufield) => ufield.field.field === field.field
                      )?.option_value
                    }
                    actionStatus={status}
                  />
                ))}
            </Stack>
          </HideContainer>
        </>
      )}
      {hasSubJourneyVariables && (
        <>
          <Sidebar.Separator />
          <HideContainer title={t('outputFields')}>
            <Stack space={2}>
              {Object.entries(action.variables).map(([key, variable]) => (
                <LabelTextGroup
                  key={key}
                  label={variable.label}
                  text={formatOutputFieldValue(action.data?.[key], variable.type)}
                />
              ))}
            </Stack>
          </HideContainer>
        </>
      )}
      {attributesMissing.length > 0 && status !== 'completed' && (
        <HideContainer title={t('fieldsMissing')}>
          <Stack space={2}>
            <AddToJourneyInfoBox infoType="fieldMissing" />
            {attributesMissing.map((attribute, idx) => (
              <Text key={idx} font="regular14" text={attribute.title} />
            ))}
          </Stack>
        </HideContainer>
      )}
      <Sidebar.Separator />
      <HideContainer title={t('configuration')}>
        <Stack space={4}>
          <LabelTextGroup
            label={t('startWhenAdded')}
            text={action.values?.startJourney ? t('core.yes') : t('core.no')}
          />
          <LabelTextGroup
            label={t('waitUntilComplete')}
            text={action.values?.waitUntilComplete ? t('core.yes') : t('core.no')}
          />
        </Stack>
      </HideContainer>
      <Sidebar.Separator />
      {status !== 'completed' && (
        <TravellerActionFooter
          action={action}
          startIcon={<IconSend />}
          startText={t('run')}
          retryText={t('retry')}
        />
      )}
    </Stack>
  );
};

const FormContent = ({ action }: ContentProps) => {
  const { stopEditing } = useEditingContext();

  const editMutation = useUpdateJourneyTravellerAction(action, { onSuccess: stopEditing });

  const filePath = createTravellerJourneyActionFieldPath(action);

  return (
    <AddToJourneyFormContent
      originalJourney={action.journeyId}
      action={action}
      filePath={filePath}
      onCancel={stopEditing}
      hideActionButtons
      onSubmit={(data) =>
        editMutation.mutateAsync({
          isAutomatic: true,
          action: 'add_to_journey',
          values: data.values,
          description: data.description,
        })
      }
    />
  );
};

const sidebarLocale = {
  en: {
    dataNotFound: 'Data not found',
    actionDetails: 'Action details',
    description: 'Description',
    journeyDetails: 'Journey details',
    journey: 'Journey',
    journeyNotFoundInfo:
      "The selected journey has either been removed or is in a workspace you don't have access to",
    journeyNotFound: 'Journey not found',
    travellerNotInJourney: 'The traveller is no longer in this journey',
    fields: 'Input fields',
    outputFields: 'Output fields',
    noJourneyAttributes: 'There are no journey fields for this journey',
    noValidJourneyAttributes: 'The journey fields set up no longer exist',
    configuration: 'Configuration',
    startWhenAdded: 'Start journey when added',
    waitUntilComplete: 'Wait until journey completes',
    progress: 'Progress',
    run: 'Run',
    retry: 'Retry',
    fieldsMissing: 'Missing fields',
    completed: 'Completed',
    in_progress: 'In progress',
    not_started: 'Not started',
    paused: 'Paused',
    travellerDetails: 'Traveller details',
    travellerName: 'Traveller name',
    travellerEmail: 'Traveller email',
  },
};
